import React, {createContext, FC, useContext, useEffect} from "react";
import {User} from "../User";
import {checkDarkMode} from "../../App";
import {useDispatch} from "react-redux";
import {changeLanguage} from "../../reducers/translations";

export type UserContextProps = {
    user: User,
    children?: React.ReactNode
}

export interface UserInterface {
    setUser: (u: User) => void
    user: User
}

// create context
const UserContext = createContext<UserInterface | null>(null);

const useUserContext = () => {
    const context = useContext(UserContext);

    if (context === undefined) {
        throw new Error("useUserContext was used outside of its Provider");
    }

    return context;
};

const withDefaultPermissions = (u: User): User => ({
    ...u,
    permissions: Array.isArray(u.permissions) ? u.permissions : [],
    roles: Array.isArray(u.roles) ? u.roles : []
});

const UserContextProvider: FC<UserContextProps> = ({user, children}: UserContextProps) => {
    const dispatch = useDispatch();
    const [localUser, setLocalUser] = React.useState<User>(withDefaultPermissions(user));

    const setUser = (u: User) => {
        const nextUser = {...localUser, ...u};
        setLocalUser(nextUser);
        if (nextUser.theme) {
            localStorage.setItem('color-theme', nextUser.theme.toLowerCase());
            checkDarkMode();
        }
        if (nextUser.language) {
            dispatch(changeLanguage(nextUser.language));
        }
    }

    useEffect(() => {
        if (localUser && !localUser.two_fa && window.location.pathname !== '/app/admin/profile') {
            window.location.replace('/app/admin/profile');
        }
    }, [localUser, window.location.pathname]);

    return (
        <UserContext.Provider value={{user: localUser, setUser}}>
            {children}
        </UserContext.Provider>
    );
};

export {useUserContext, UserContextProvider};
